import { z } from 'zod'

import type { SchemaClient } from '../client'

export const SCHOOL_SUPPLY_MENU_KEY = 'menus.supply-menu.v1'
export const SUPPLY_TEMPLATE_PREFIX = 'menus.supply-template.v1.'
export const SupplySelectionSchema = z
  .object({
    minimumCases: z.number().int().min(1).max(10000).nullable().optional(),
    name: z.string().trim().min(1).max(120),
    sourceTemplateId: z.string().optional(),
    items: z
      .array(
        z.object({
          supplyItemId: z.string().min(1),
          available: z.boolean(),
          // Optional for existing selections; new saves snapshot the catalog default.
          customerPriceCents: z.number().int().min(0).max(2147483647).optional(),
        }),
      )
      .max(500),
  })
  .refine(
    (value) => new Set(value.items.map((item) => item.supplyItemId)).size === value.items.length,
    'Select each item once',
  )

/** No configured menu preserves the existing national catalog. An invalid stored
 * configuration fails closed so a damaged setting cannot broaden availability. */
export type SupplySelection = z.infer<typeof SupplySelectionSchema>
export async function readSchoolSupplyItems(
  db: SchemaClient,
  organizationId: string,
): Promise<SupplySelection['items'] | null> {
  const row = await db.orgSetting.findUnique({
    where: { organizationId_key: { organizationId, key: SCHOOL_SUPPLY_MENU_KEY } },
  })
  if (!row) {
    return null
  }
  const parsed = SupplySelectionSchema.safeParse(row.valueJson)
  return parsed.success ? parsed.data.items.filter((item) => item.available) : []
}

export async function schoolSupplyItemIds(db: SchemaClient, organizationId: string): Promise<string[] | null> {
  const items = await readSchoolSupplyItems(db, organizationId)
  return items?.map((item) => item.supplyItemId) ?? null
}

/** Both catalog display and checkout use the saved school price, including zero. */
export function schoolSupplyPrice(
  items: SupplySelection['items'] | null,
  itemId: string,
  catalogPriceCents: number,
): number {
  return items?.find((item) => item.supplyItemId === itemId)?.customerPriceCents ?? catalogPriceCents
}
